import type { Chassis, Resource, Role } from "../schema/common.js";

/** Game-content imagery uses native renders and authored illustrations; controls use the client SVG set. */
export type InterfaceArtId = `ui.${string}` | `emote.${string}` | `itm.${string}` | `bld.${string}` | `zmb.${string}` | "vault";

export const UI_RESOURCE_ART = {
  RAT: "ui.res.rat", POL: "ui.res.pol", ALY: "ui.res.aly", ENC: "ui.res.enc",
  SCR: "ui.res.scr", CREDITS: "ui.res.credits",
} as const satisfies Readonly<Record<Resource | "CREDITS", InterfaceArtId>>;

export const UI_STAT_ART = {
  power: "emote.bolt", stamina: "itm.stamina_50", star: "ui.stat.star",
} as const satisfies Readonly<Record<string, InterfaceArtId>>;

export const UI_ROLE_ART = {
  commander: "emote.crown", defender: "emote.shield", attacker: "ui.role.attacker",
  saboteur: "ui.role.saboteur", support: "ui.role.support",
} as const satisfies Readonly<Record<Role, InterfaceArtId>>;

export const UI_CHASSIS_ART = {
  tank: "ui.chassis.tank", mecha: "ui.chassis.mecha", tripod: "ui.chassis.tripod",
  android: "ui.chassis.android", drone_carrier: "ui.chassis.drone_carrier",
} as const satisfies Readonly<Record<Chassis, InterfaceArtId>>;

/** Keys are the actual profile/store frame IDs, so consumers need no suffix heuristics. */
export const UI_FRAME_ART = {
  steel: "ui.frame.steel", bronze: "ui.frame.bronze", silver: "ui.frame.silver",
  gold: "ui.frame.gold", neon: "ui.frame.neon", void: "ui.frame.void", mega: "ui.frame.mega",
  "cos.frame.gladiator": "ui.frame.gladiator", "cos.frame.nexus": "ui.frame.nexus",
  "cos.frame.collector": "ui.frame.collector",
  "cos.frame.alliance": "ui.frame.alliance",
  "cos.frame.duel_mvp": "ui.frame.duel_mvp",
} as const satisfies Readonly<Record<string, InterfaceArtId>>;

export const UI_MEDAL_ART = {
  gold: "ui.medal.gold", silver: "ui.medal.silver", bronze: "ui.medal.bronze",
} as const satisfies Readonly<Record<string, InterfaceArtId>>;

/** Event identity is shared by live cards, the calendar and the Blender contact sheets. */
export const UI_EVENT_ART = {
  "ev.daily_missions": "ui.event.clipboard", "ev.dawn": "ui.event.dawn",
  "ev.intel": "bld.intel_hub", "ev.login": "ui.event.calendar",
  "ev.arms_race": "bld.armory", "ev.siege": "emote.zombie", "ev.alliance_duel": "emote.shield",
  "ev.elite_hunt": "emote.target", "ev.supply_run": "ui.event.truck",
  "ev.hourly_hunt": "emote.bolt",
  "ev.arena_season": "bld.arena_terminal", "ev.manufacturer_week": "bld.workshop",
  "ev.fab_fest": "itm.ffab", "ev.lucky_market": "ui.event.coupon", "ev.key_gacha": "itm.chest_gold",
  "ev.break_loose": "itm.speedup_general_60", "ev.horde_night": "ui.event.moon",
  "ev.titan": "zmb.boss_titan_necrolith", "ev.trial": "ui.event.tower",
  "ev.chapter": "ui.event.book", "ev.fortune_vault": "bld.storage_vault",
  "ev.relic_war": "vault", "ev.salvage_rush": "emote.wrench",
  "ev.season": "emote.trophy", "ev.necro_carnival": "ui.event.pumpkin",
  "ev.frostfall": "ui.event.snowflake", "ev.anniversary": "ui.event.cake",
  "ev.summer_blackout": "ui.event.eclipse", "ev.newcomer": "emote.starburst",
  "ev.comeback": "bld.command_center",
} as const satisfies Readonly<Record<string, InterfaceArtId>>;

export const UI_EQUIPMENT_ART = { weapon: "ui.equipment.weapon", armor: "ui.equipment.armor", core: "ui.equipment.core", sensor: "ui.equipment.sensor", drive: "ui.equipment.drive" } as const;

/** One physical mechanism per manufacturer/slot; rank remains the owned item's frame. */
export const UI_MODULE_ART: Readonly<Record<string, InterfaceArtId>> = Object.fromEntries(
  ["HEX", "KRG", "VLK", "OMC", "RDL"].flatMap(mfr => Object.keys(UI_EQUIPMENT_ART).map(slot =>
    [`${mfr}_${slot}`, `ui.module.${mfr.toLowerCase()}_${slot}` as InterfaceArtId])),
);

export function moduleArt(mfr: string, slot: keyof typeof UI_EQUIPMENT_ART): InterfaceArtId {
  return UI_MODULE_ART[`${mfr}_${slot}`] ?? UI_EQUIPMENT_ART[slot];
}

/** All referenced art, including exact existing motifs. Only ui.* needs new exports. */
export const INTERFACE_ASSET_IDS: readonly InterfaceArtId[] = [...new Set([
  ...Object.values(UI_RESOURCE_ART), ...Object.values(UI_STAT_ART),
  ...Object.values(UI_ROLE_ART), ...Object.values(UI_CHASSIS_ART),
  ...Object.values(UI_FRAME_ART), ...Object.values(UI_MEDAL_ART),
  ...Object.values(UI_EVENT_ART), ...Object.values(UI_EQUIPMENT_ART), ...Object.values(UI_MODULE_ART),
])];
